/**
 * E04-F02-T05 — the "Reply to email" dialog.
 *
 * 1:1 port of the legacy `inbox.js` showReplyDialog + send_reply. To / Subject /
 * Body fields pre-filled from the email (To = sender, Subject = "Re: …"); the
 * body must be non-empty before Send (acceptance — validation of a non-empty body)
 * and Send shows a spinner while in flight. The SENDER is the system mailbox
 * (never a body field — IDOR), so the dialog only carries to/subject/body +
 * threading ids. On success → toast + the conversation reloads (the mutation
 * invalidates the emails domain).
 */
import { useState, type JSX } from 'react';
import type { EmailDTO } from '@eer/shared';
import { Dialog } from '../ui/Dialog.js';
import { RichTextEditor } from '../ui/RichTextEditor.js';
import { useToast } from '../ui/ToastContext.js';
import { useReplyEmail } from './inboxActionsApi.js';

const FIELD_CLASS =
  'w-full rounded-md border border-[var(--color-border)] p-2 text-sm outline-none focus:border-[var(--color-accent)]';

export interface ReplyEmailDialogProps {
  readonly email: EmailDTO;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSent?: () => void;
}

/** Re-prefix a subject with "Re: " unless it already has one (case-insensitive). */
function replySubject(subject: string | null | undefined): string {
  const s = (subject ?? '').trim();
  if (!s) return 'Re:';
  return /^re:/i.test(s) ? s : `Re: ${s}`;
}

export function ReplyEmailDialog({
  email,
  open,
  onClose,
  onSent,
}: ReplyEmailDialogProps): JSX.Element {
  const toast = useToast();
  const reply = useReplyEmail();

  const [to, setTo] = useState(email.sender ?? '');
  const [subject, setSubject] = useState(() => replySubject(email.subject));
  const [body, setBody] = useState('');

  function handleSend(): void {
    if (!to.trim()) {
      toast.error('Recipient is required');
      return;
    }
    // Non-empty body validation (acceptance).
    if (!body.trim()) {
      toast.error('Reply body cannot be empty');
      return;
    }
    reply.mutate(
      {
        emailId: email.id,
        body: {
          to: to.trim(),
          subject,
          body,
          ...(email.external_id ? { in_reply_to: email.external_id } : {}),
          ...(email.thread_id ? { thread_id: email.thread_id } : {}),
        },
      },
      {
        onSuccess: () => {
          toast.success('Reply sent');
          onSent?.();
          onClose();
        },
        onError: (err) => toast.error(err.detail.message || 'Reply failed'),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Reply"
      testId="reply-email-dialog"
      maxWidth="2xl"
      footer={
        <>
          <button
            type="button"
            data-testid="reply-email-cancel"
            onClick={onClose}
            className="rounded border border-[var(--color-border)] bg-white px-4 py-2 text-sm font-medium hover:bg-[var(--color-surface-muted)]"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="reply-email-send"
            onClick={handleSend}
            disabled={reply.isPending}
            className="inline-flex items-center gap-2 rounded bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--color-primary-hover)] disabled:opacity-60"
          >
            {reply.isPending ? (
              <>
                <i
                  className="fa-solid fa-spinner fa-spin"
                  aria-hidden
                  data-testid="reply-email-spinner"
                />
                Sending…
              </>
            ) : (
              'Send'
            )}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-[var(--color-text-strong)]">To</span>
          <input
            type="text"
            data-testid="reply-email-to"
            className={FIELD_CLASS}
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-[var(--color-text-strong)]">Subject</span>
          <input
            type="text"
            data-testid="reply-email-subject"
            className={FIELD_CLASS}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-[var(--color-text-strong)]">Body</span>
          {/* Этап 4 / KS-07: ответ клиенту — HTML (тот же редактор, что и в
              остальных письмах, чтобы форматирование вело себя одинаково). */}
          <RichTextEditor
            testId="reply-email-body"
            ariaLabel="Reply body"
            minHeightClass="min-h-[160px]"
            placeholder="Type your reply…"
            value={body}
            onChange={setBody}
          />
        </label>
      </div>
    </Dialog>
  );
}

export default ReplyEmailDialog;
